<template>
	<ul class="navbar-nav menu-tabs">
		<li v-for="tab in visibleTabs" :key="tabKey(tab)" class="nav-item">
			<a href="#" class="nav-link" :class="{ active: isActive(tab) }" @click.prevent="goToTab(tab)">
				<span class="nav-label text-truncate">{{ $t(tab.label) }}</span>
				<span v-if="tab.add_action" class="nav-add" role="button" @click.stop.prevent="tab.add_action()">
					<font-awesome-icon :icon="['fal', 'plus']" />
				</span>
				<font-awesome-icon :icon="['fal', chevronIcon(tab)]" class="nav-chevron" />
			</a>
			<!-- Second niveau, à l'image du volet des onglets desktop (Swiper). Le sous-onglet par défaut
			     y figure comme les autres : aucune destination n'est perdue par rapport au tap direct. -->
			<ul v-if="isOpen(tab)" class="navbar-nav menu-sub-tabs">
				<li v-for="sub_tab in tab.sub_tabs" :key="sub_tab.label" class="nav-item">
					<a href="#" class="nav-link" :class="{ active: matchRoute(sub_tab) }" @click.prevent="navigate(sub_tab)">
						<span class="nav-label text-truncate">{{ $t(sub_tab.label) }}</span>
						<b-badge v-if="sub_tab.badge !== undefined" pill variant="primary" class="ml-1">{{ sub_tab.badge }}</b-badge>
						<font-awesome-icon :icon="['fal', 'angle-right']" class="nav-chevron" />
					</a>
				</li>
			</ul>
		</li>
	</ul>
</template>

<script type="text/javascript">
	export default {
		name: 'MenuTabsMobile',
		props: {
			swiper_tabs: { type: Array, default: () => [] },
			action: { type: Function, default: null } /* Callback des onglets pilotés par action (cf. prop swiper_action du HeaderTab) */,
			add_action: { type: Function, default: null } /* Retourne l'action d'ajout rapide d'un onglet, null s'il n'en propose pas */
		},
		data () {
			return {
				open_tab: null /* Onglet dont le second niveau est déplié, un seul à la fois */
			}
		},
		methods: {
			/**
			 * Déplie le second niveau de l'onglet sélectionné, ou navigue vers celui-ci s'il n'en a pas
			 * @param {Object} tab Onglet du swiper de la page
			 */
			goToTab(tab) {
				if(this.action) {
					this.action(tab)
					this.$emit('close')
					return
				}

				if(tab.sub_tabs.length > 0) {
					this.open_tab = this.isOpen(tab) ? null : this.tabKey(tab)
					return
				}

				this.navigate(tab)
			},
			/**
			 * Affiche la vue de l'onglet et referme le menu. Un onglet déjà affiché ne redéclenche pas la
			 * navigation, que le routeur rejetterait.
			 * @param {Object} tab Onglet ou sous-onglet du swiper de la page
			 */
			navigate(tab) {
				if(!this.matchRoute(tab)) {
					this.$router.replace({ name: tab.href, params: tab.params })
				}
				this.$emit('close')
			},
			/**
			 * Identifie un onglet parmi ses pairs, pour la boucle d'affichage comme pour le dépliage
			 * @param  {Object} tab Onglet du swiper de la page
			 * @return {String}
			 */
			tabKey(tab) {
				return tab.href || tab.label
			},
			/**
			 * Indique si le second niveau de l'onglet est déplié
			 * @param  {Object} tab Onglet du swiper de la page
			 * @return {Boolean}
			 */
			isOpen(tab) {
				return this.open_tab === this.tabKey(tab)
			},
			/**
			 * Chevron de la ligne : il annonce un second niveau à déplier ou replier, sinon une navigation
			 * @param  {Object} tab Onglet du swiper de la page
			 * @return {String}
			 */
			chevronIcon(tab) {
				if(tab.sub_tabs.length === 0) {
					return 'angle-right'
				}
				return this.isOpen(tab) ? 'angle-up' : 'angle-down'
			},
			/**
			 * Indique si l'onglet correspond à la vue affichée : drapeau posé par la page en mode action,
			 * sinon route courante comparée à l'onglet ou à ses sous-onglets
			 * @param  {Object} tab Onglet du swiper de la page
			 * @return {Boolean}
			 */
			isActive(tab) {
				if(this.action) {
					return tab.active === true
				}
				if(tab.sub_tabs.length > 0) {
					return tab.sub_tabs.some(sub_tab => this.matchRoute(sub_tab))
				}
				return this.matchRoute(tab)
			},
			/**
			 * Vrai si l'onglet pointe sur la route courante, en appliquant la même règle que les onglets desktop (Swiper)
			 * @param  {Object} tab Onglet du swiper de la page
			 * @return {Boolean}
			 */
			matchRoute(tab) {
				const route_name = this.$route.name
				const match_route = tab.href === route_name || (tab.active_routes !== undefined && tab.active_routes.includes(route_name))
				if(!match_route) {
					return false
				}
				// Deux onglets peuvent viser la même route avec des paramètres différents — le cas des vues
				// de factures fournisseurs. Les valeurs sont comparées une à une et en chaînes : la route
				// les porte toujours ainsi, là où un écran peut les fournir en nombres.
				if(!tab.params) {
					return true
				}
				return Object.keys(tab.params).every(key => String(tab.params[key]) === String(this.$route.params[key]))
			}
		},
		computed: {
			/**
			 * Onglets affichables, enrichis de leur action d'ajout et de leurs sous-onglets visibles.
			 * Un onglet dont le sous-menu est absent ou entièrement masqué se retrouve sans sous-onglet
			 * et se comporte donc comme un onglet simple : il n'aurait rien à déplier.
			 * @return {Array}
			 */
			visibleTabs() {
				return this.swiper_tabs
					.filter(tab => !tab.hidden && tab.label)
					.map(tab => Object.assign({}, tab, {
						add_action: this.add_action ? this.add_action(tab) : null,
						sub_tabs: tab.dropdown ? tab.dropdown.filter(sub_tab => !sub_tab.hidden) : []
					}))
			}
		},
		created() {
			// Ouvrir le menu depuis un sous-onglet doit montrer où l'on se trouve, second niveau déplié :
			// sans cela on ne voit que l'onglet parent, et rien n'indique la position courante.
			if(this.action) {
				return
			}

			const active_tab = this.visibleTabs.find(tab => tab.sub_tabs.some(sub_tab => this.matchRoute(sub_tab)))

			if(active_tab) {
				this.open_tab = this.tabKey(active_tab)
			}
		}
	}
</script>
